import {
  type ProfileOrderIndexLimits,
  OrderingLimitExceededError,
  ProfileOrderIndex,
  resolveMaxNodeVisits,
  resolveMaxObjectKeysPerObject,
  resolveMaxRelaxedPathEntries
} from "./esim-lookup.ordering.profile-order-index";
import { TemplateOrderer } from "./esim-lookup.ordering.template-orderer";
import { resolveMaxRelaxedPathCacheEntries } from "./esim-lookup.ordering.wildcards";

type OrderingLimitExceededHandler = (error: OrderingLimitExceededError) => void;
type StructuredCloneFn = <T>(value: T) => T;

export type OrderTemplateByProfileOptions = {
  maxNodeVisits?: number;
  maxObjectKeysPerObject?: number;
  maxRelaxedPathEntries?: number;
  maxRelaxedPathCacheEntries?: number;
  onLimitExceeded?: OrderingLimitExceededHandler;
};

export {
  DEFAULT_MAX_NODE_VISITS,
  DEFAULT_MAX_OBJECT_KEYS_PER_OBJECT,
  DEFAULT_MAX_RELAXED_PATH_ENTRIES
} from "./esim-lookup.ordering.profile-order-index";
export { DEFAULT_MAX_RELAXED_PATH_CACHE_ENTRIES } from "./esim-lookup.ordering.wildcards";

function cloneTemplateSafely<T>(templateJson: T): T {
  const structuredCloneFn = (globalThis as { structuredClone?: StructuredCloneFn }).structuredClone;
  if (typeof structuredCloneFn === "function") {
    try {
      return structuredCloneFn(templateJson);
    } catch {
      // Fallback to JSON clone below for environments or values unsupported by structuredClone.
    }
  }

  try {
    return JSON.parse(JSON.stringify(templateJson)) as T;
  } catch {
    // Keep guard fallback non-throwing even for non-serializable values.
    return templateJson;
  }
}

// Orders template keys to follow profile key ordering while preserving nested structure.
export function orderTemplateByProfile<T>(profileJson: unknown, templateJson: T, options: OrderTemplateByProfileOptions = {}): T {
  const limits: ProfileOrderIndexLimits = {
    maxNodeVisits: resolveMaxNodeVisits(options.maxNodeVisits),
    maxObjectKeysPerObject: resolveMaxObjectKeysPerObject(options.maxObjectKeysPerObject),
    maxRelaxedPathEntries: resolveMaxRelaxedPathEntries(options.maxRelaxedPathEntries),
    maxRelaxedPathCacheEntries: resolveMaxRelaxedPathCacheEntries(options.maxRelaxedPathCacheEntries)
  };

  try {
    const profileOrderIndex = ProfileOrderIndex.fromProfile(profileJson, limits);
    return new TemplateOrderer(profileOrderIndex, limits.maxNodeVisits, limits.maxObjectKeysPerObject).order(templateJson);
  } catch (error) {
    if (error instanceof OrderingLimitExceededError) {
      options.onLimitExceeded?.(error);
      console.warn(`[orderTemplateByProfile] ${error.message} Returning template JSON without ordering applied.`);
      return cloneTemplateSafely(templateJson);
    }

    throw error;
  }
}
